import { Endpoint, Entity } from "@rest-hooks/rest";
import { api_url, auth_headers } from "src/context";
import { BlueprintProgramData } from "src/routes/cma/steps/ready-plan/v2/endpoints/BlueprintReadyPlansEndpoint";
import { Maybe } from "src/utils";

export interface ReadyPlanChildOption {
  id: string;
  name: string;
  active: boolean;
}

export interface RPOptionConflictPrerequisite {
  id: string;
  displayName: string;
  type: { name: string };
}

export interface ReadyPlanOptionBase {
  id: string;
  name: string;
  displayName: string;
  code: string;
  active: boolean;
  type: { name: string };
}

export interface ReadyPlanOption extends ReadyPlanOptionBase {
  // Can ONLY be selected when all of these options are selected
  optionPrerequisites: RPOptionConflictPrerequisite[];
  // Cannot be selected in conjunction with any of these options
  optionConflicts: RPOptionConflictPrerequisite[];
  programData: Maybe<Partial<BlueprintProgramData>>;
  optionDefaultChildren: ReadyPlanOptionBase[];
  optionDefaultsIf: ReadyPlanOptionBase[];
  publishToUnderwriting: boolean;
}

export interface GlobalOptionGroupType {
  name: string;
  isElevation: boolean;
  isSpecLevel: boolean;
  isExteriorPalette: boolean;
  isInterior: boolean;
  isAddOnV2: boolean;
  isStructuralV2: boolean;
}

export interface GlobalOptionGroup {
  type: GlobalOptionGroupType;
  forDesignPackages: boolean;
}

export class BlueprintReadyPlanOptionGroup extends Entity {
  readonly id: string = "";
  readonly name: string = "";
  readonly required: boolean = false;
  readonly options: ReadyPlanOption[] = [];
  readonly isSingleOptionGroup: boolean = false;
  readonly isMultiOptionGroup: boolean = false;
  readonly order: number = 0;
  readonly group: GlobalOptionGroup | undefined = undefined;

  pk() {
    return this.id;
  }

  static key = "BlueprintReadyPlanOptionGroup";
}

export function isBlueprintRPOGRequired(og: BlueprintReadyPlanOptionGroup, devId: string): boolean {
  // If we're building this in a community instead of a development, we are using H1 tech and are treating these differently
  if (devId.startsWith("p:")) {
    // This includes the Elevation, Exterior Scheme, Interior Scheme, and Spec Level groups
    return og.group!.forDesignPackages;
  }
  return og.required;
}

export interface BlueprintOptionGroupsEndpointOptions {
  devId: string;
  bp_ready_plan_id: string;
}

export const BlueprintOptionGroupsEndpoint = new Endpoint(
  async ({ devId, bp_ready_plan_id }: BlueprintOptionGroupsEndpointOptions) => {
    const headers = await auth_headers();

    const res = await fetch(api_url(`/v2/ready_plans/developments/${devId}/ready_plans/${bp_ready_plan_id}/options`), {
      headers,
    });

    return await res.json();
  },
  {
    schema: { optionGroups: [BlueprintReadyPlanOptionGroup] },
    name: "BlueprintOptionGroupsEndpoint",
  },
);
